import { ref, computed, watch, type Ref } from 'vue'
import { getPatentMatrixData } from '@/infrastructure/api-functions'
import { E_ANALYSIS_CHART, PATENT_EFFICIENCY_TYPE } from '@/types'
import { downloadImage } from '@eureka/utils/download-image'
import { useGlobalState } from './use-global-store';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { useI18n } from 'vue-i18n';
import { generateImgTitle } from '@/infrastructure/utils/generate-img-title';

export function usePatentMatrix(chartComponent: Ref<{
    generateChartImage: () => Promise<string>,
    drawChart: () => void,
    isChartLoaded: () => boolean
    isEmptyChart: boolean
} | undefined>) {
    const { t } = useI18n();

    const title = t('patentTrend.title');

    const { checkedChild, companyId } = useGlobalState();

    const chartKey = E_ANALYSIS_CHART.PATENT_MATRIX

    const isFetchLoading = ref(false)

    const isDownloadingImage = ref(false)

    const requestId = ref(0)

    const disableUI = computed(() => {
        return (!chartComponent.value?.isChartLoaded && !isFetchLoading.value) || chartComponent.value?.isEmptyChart
    })

    /**
     * 关键效果 / 全部效果
     */
    const efficiencyType = ref(PATENT_EFFICIENCY_TYPE.KEY_EFFICIENCY)
    const isKeyEfficiency = computed(() => {
        return efficiencyType.value === PATENT_EFFICIENCY_TYPE.KEY_EFFICIENCY
    })

    function changeEfficiencyType(type: PATENT_EFFICIENCY_TYPE) {
        if (disableUI.value || efficiencyType.value === type) return
        efficiencyType.value = type
        chartComponent.value?.drawChart()
    }

    function changeChild() {
        chartComponent.value?.drawChart()
    }

    function chartConfig() {
        return import('@/constant/chart/chartConfigMatrix.json').then((d) => { return d.default });
    }

    function getPatentMatrixChartData() {
        isFetchLoading.value = true
        requestId.value++
        const currentRequestId = requestId.value
        if (currentRequestId === requestId.value) {
            return getPatentMatrixData({
                type: efficiencyType.value,
                child: checkedChild.value,
                company_id: companyId.value,
            }).finally(() => {
                isFetchLoading.value = false
            })
        }
    }

    async function downloadChartAsImage() {
        if (isDownloadingImage.value) return
        isDownloadingImage.value = true
        try {
            const imageStr = await chartComponent.value?.generateChartImage()
            if (imageStr) {
                downloadImage(imageStr, generateImgTitle(title))
            }
        } finally {
            isDownloadingImage.value = false
        }
    }

    function handleDrillDown(data: Record<string, string>) {
        const { firstCode, secondCode } = data
        openRightAside({
            title: t('patent'),
            data: {
                company_id: companyId.value,
                chart_type: isKeyEfficiency.value ? E_ANALYSIS_CHART.KEY_BENEFIT_PHRASE_ADC_C : E_ANALYSIS_CHART.BENEFIT_PHRASE_ADC_C,
                include_children: checkedChild.value,
                dimension_list: [
                    {
                        field: isKeyEfficiency.value ? 'BENEFIT_PT_ID' : 'BENEFIT_PHRASE_ID',
                        value: firstCode,
                    },
                    {
                        field: 'ADC_C',
                        value: secondCode,
                    }
                ],
            },
        })
    }

    watch(
        () => checkedChild.value,
        () => {
            chartComponent.value?.drawChart()
        }
    )

    return {
        title,
        companyId,
        disableUI,
        isFetchLoading,
        chartKey,
        chartConfig,
        efficiencyType,
        getPatentMatrixChartData,
        changeEfficiencyType,
        changeChild,
        downloadChartAsImage,
        handleDrillDown,
    }
}
